import React, { useState, useEffect, useContext } from "react";
import { useTheme } from "@mui/material/styles";
import clsx from "clsx";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";

import { getColor } from "../tools";
import AnalyserContext from "./analyserContext";
import SearchField from "../Components/SearchField";
import { useTranslation } from "react-i18next";
import { questionTypes } from "../Editor/itemTypesAddButton";
import useClasses from "../useClasses";

const styles = (theme) => ({
  icon: {
    color: theme.palette.text.secondary,
  },
  expand: {
    color: theme.palette.text.secondary,
    //padding: theme.spacing(1),
    transform: "rotate(-90deg)",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: "rotate(0deg)",
  },
});

function CustomList({ title, initList, initExpanded = true, type, selected }) {
  const classes = useClasses(styles);
  const theme = useTheme();
  const { t } = useTranslation();
  const [list, setList] = useState(initList);
  const [expanded, setExpanded] = useState(initExpanded);

  useEffect(() => setList(initList), [initList]);

  const handleExpandClick = () => setExpanded((prev) => !prev);

  const handleSearch = (value) => {
    const filteredEvents = initList.filter((item) =>
      item.title.toLowerCase().includes(value.toLowerCase())
    );
    setList(filteredEvents);
  };

  return (
    <List dense>
      <ListItem
        button
        onClick={handleExpandClick}
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            className={clsx(classes.expand, {
              [classes.expandOpen]: expanded,
            })}
            style={{ marginRight: 8 }}
          >
            <ExpandMoreOutlinedIcon fontSize="small" />
          </div>
          <Typography noWrap variant="subtitle1" style={{ fontWeight: 500 }}>
            {title}
          </Typography>
        </div>
        {list.length > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={`${selected.length}/${initList.length}`}
          />
        ) : null}
      </ListItem>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {initList.length > 0 ? (
          <div style={{ margin: theme.spacing(1, 2, 2, 2) }}>
            <SearchField
              setUserText={handleSearch}
              placeholder={t("common.search")}
              variant={"searchClient"}
            />
          </div>
        ) : null}
        {list.length > 0 ? (
          list.map((item) => (
            <CustomListItem
              key={item.id}
              item={item}
              type={type}
              checked={selected.includes(item.id)}
            />
          ))
        ) : (
          <ListItem>
            <Typography variant="body2" color="textSecondary">
              {t("common.noResults")}
            </Typography>
          </ListItem>
        )}
      </Collapse>
    </List>
  );
}

function listsAreEqual(prevList, nextList) {
  return (
    JSON.stringify(prevList.initList) === JSON.stringify(nextList.initList) &&
    JSON.stringify(prevList.selected) === JSON.stringify(nextList.selected)
  );
}

const CustomListItem = ({ item, type, checked }) => {
  const classes = useClasses(styles);
  const { t } = useTranslation();
  const labelId = `checkbox-list-label-${item.id}`;
  const { moodboardDispatch } = useContext(AnalyserContext);
  const handleToggle = () =>
    moodboardDispatch({
      type: "TOGGLE_LIST",
      payload: { type, ids: [item.id], item },
    });
  const getIcon = () => {
    if (type === "itemId") {
      if (item.type === 2) return <TouchAppOutlinedIcon />;
      return "";
    }
    return questionTypes[item.type]?.icon ?? "";
  };

  const getTitle = () => {
    if (type === "itemId") {
      if (item.type === 2) return t(`create.button`);
      return "";
    }
    return t(`editor.questionTypes.${item.type}.title`);
  };

  return (
    <ListItem role={undefined} dense button onClick={handleToggle}>
      <ListItemIcon>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            edge="start"
            checked={checked}
            color="primary"
            tabIndex={-1}
            disableRipple
            inputProps={{ "aria-labelledby": labelId }}
          />
          {/* Label dot for the quantitative items */}
          {type === "quantitative" && checked ? (
            <div
              style={{
                height: "12px",
                width: "12px",
                backgroundColor: getColor(item.id),
                marginRight: "5px",
                marginTop: "3px",
                borderRadius: "50%",
                display: "flex",
              }}
            />
          ) : null}
        </div>
      </ListItemIcon>
      {/* Hoover on the label to view the question if the item has a type */}
      {type === "itemId" ? (
        <ListItemText id={labelId} primary={item.title} />
      ) : (
        <Tooltip title={item.question} placement="bottom-start">
          {/*  Label */}
          <ListItemText id={labelId} primary={item.title} />
        </Tooltip>
      )}
      {/* Show the question type if defined */}
      <Tooltip title={getTitle()}>
        <div className={classes.icon}>{getIcon()}</div>
      </Tooltip>
    </ListItem>
  );
};

export default React.memo(CustomList, listsAreEqual);
